import { Component, inject, computed, signal, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AssetVersionPipe } from '../../../../pipes/asset-version.pipe';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { CopilotService } from '../../../../services/copilot.service';
import { PolarisRoutePaths, RoutePaths } from '../../../../shared/types/common';
import { POLARIS_NAME } from '../../copilot.model';
import { MatTooltip } from '@angular/material/tooltip';
import { tooltips } from '../../../../utils/tooltips';

@Component({
  selector: 'app-chat-history',
  standalone: true,
  imports: [CommonModule, MatTooltip, AssetVersionPipe],
  templateUrl: './chat-history.component.html',
  styleUrl: './chat-history.component.scss'
})
export class ChatHistoryComponent {
  private copilotService = inject(CopilotService);
  private router = inject(Router);
  public polarisName = POLARIS_NAME;
  private hoverTimeout: any;
  private movedToChat = false;

  protected readonly tooltips = tooltips;
  public scrolledToBottom = false;
  public isScrollable = false;
  public showHistory = false;
  public searchTerm = signal('');
  public filteredChats = computed(() => {
    const search = this.searchTerm().toLowerCase();
    const allChats = Object.values(this.copilotService.chats())
      .sort((a, b) => b.lastUpdate - a.lastUpdate);

    if (!search) return allChats;

    return allChats.filter(chat =>
      chat.title.toLowerCase().includes(search)
    );
  });
  public noChatsMessage = computed(() => {
    return this.searchTerm() ? 'No chats match your search' : 'Your chat history is empty';
  });
  public noChatsSubMessage = computed(() => {
    return this.searchTerm() ? 'Try a different term or phrase' : `Ask ${this.polarisName} anything about your brand,`;
  });
  public noChatsExtraMessage = computed(() => {
    return this.searchTerm() ? '' : 'campaigns, or competitors to begin';
  });

  @ViewChild('chatList') chatList!: ElementRef<HTMLDivElement>;

  onScroll() {
    this.updateScrollable();
  }

  updateScrollable() {
    const el = this.chatList?.nativeElement;
    if (!el) return;

    const threshold = 2;
    const scrollable = el.scrollHeight > el.clientHeight;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;

    this.isScrollable = scrollable;
    this.scrolledToBottom = atBottom;
  }

  public navigateToChat(chatId: string): void {
    if (this.router.url.includes(chatId)) return;
    this.movedToChat = true;
    this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.CHAT}/${chatId}`]);
  }

  public navigateToHome(): void {
    this.showHistory = false;
    this.router.navigate([`${RoutePaths.POLARIS}/${PolarisRoutePaths.POLARIS_HOME}`]);
  }

  public onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }

  public isCurrentChat(chatId: string): boolean {
    const currentUrl = this.router.url;
    return currentUrl.includes(`/c/${chatId}`);
  }

  public toggleShowHistory(): void {
    this.movedToChat = false;
    this.showHistory = !this.showHistory;
    this.updateScrollable();
  }

  get isHomePage(): boolean {
    return this.router.url.includes(PolarisRoutePaths.POLARIS_HOME);
  }

  get isAlertCenterPage(): boolean {
    return this.router.url.includes(PolarisRoutePaths.ALERTS);
  }

  public trackByChatId(index: number, chat: any): string {
    return chat.id;
  }

  public onEnterHistory() {
    clearTimeout(this.hoverTimeout);
    this.showHistory = true;
  }

  public onLeaveHistory() {
    if (this.movedToChat) {
      this.hoverTimeout = setTimeout(() => {
        this.showHistory = false;
      }, 1000);
    }
  }
}
